/**
 * The Led AI lockup — the real one: hard hat over safety glasses, then the
 * LED AI wordmark.
 *
 * ## It is a MASK, not an `<img>`
 *
 * The artwork is a black-on-transparent PNG, so the alpha channel already IS
 * the shape. Painting it as a `mask-image` over a colour means the lockup can
 * take the ink of whatever it sits in — paper everywhere in the console, and
 * white on the sign-in screen's one dark panel. An `<img>` would bake one
 * colour in, and a black mark stranded on ink is no mark at all. The colour
 * is the `tone` prop, not `currentColor`; see its own note.
 *
 * `WebkitMaskImage` is set alongside the standard property: Safari still
 * needs the prefix for `mask-image` with `mask-size`, and this console is
 * read on Macs.
 *
 * ## Why a raster and not a path
 *
 * The mark has a lot of small detail — the hat's ribs and brim, the glasses'
 * bridge and rims, the stencil breaks in the wordmark — and a traced path
 * that kept it honestly would be larger than the image and worse at small
 * sizes. See `assets/lockup.ts` for why it is inline.
 *
 * The design system's `logo-mark.svg` (plain glasses, no hat) is NOT this
 * lockup and should not be substituted for it.
 */

import { LED_AI_LOCKUP_URL } from "../assets/lockup";
import { LED_AI_MARK_URL } from "../assets/mark";
import { C } from "../tokens";

/** Each artwork's own aspect, after its crop. */
const LOCKUP_ASPECT = 455 / 120;
const MARK_ASPECT = 87 / 84;

/**
 * The lockup, or the square badge mark.
 *
 * One component for both because they are one drawing at two croppings and
 * one painting technique — a second component would be a second place for the
 * mask mechanics to drift.
 */
export function Lockup({
  height = 40,
  variant = "lockup",
  tone = C.ink,
}: {
  height?: number;
  /** `mark` is the hat-and-glasses badge, for places too narrow for the
   *  wordmark beside it — the sidebar's top-left. */
  variant?: "lockup" | "mark";
  /**
   * What the mask is painted IN. Ink everywhere in the console, because
   * everything in the console is paper — except the sign-in screen's brand
   * panel, which is the one dark surface the mark sits on and where ink on
   * ink is an invisible logo (it rendered exactly that way once).
   *
   * A prop rather than `currentColor` on purpose: the mask's colour is the
   * one thing a caller must state deliberately. Inheriting it would make
   * every future dark surface a coin toss between a visible mark and a
   * blank space, decided by whatever `color` happened to be on an ancestor.
   */
  tone?: string;
}) {
  const mark = variant === "mark";
  const url = mark ? LED_AI_MARK_URL : LED_AI_LOCKUP_URL;
  return (
    <span
      data-testid={mark ? "led-mark" : "led-lockup"}
      role="img"
      aria-label="Led AI"
      style={{
        display: "block",
        height,
        width: Math.round(height * (mark ? MARK_ASPECT : LOCKUP_ASPECT)),
        background: tone,
        // The design draws the lockup a shade back from full ink — it is a
        // signature, not a heading.
        opacity: 0.9,
        maskImage: `url(${url})`,
        WebkitMaskImage: `url(${url})`,
        maskRepeat: "no-repeat",
        WebkitMaskRepeat: "no-repeat",
        maskSize: "contain",
        WebkitMaskSize: "contain",
        maskPosition: "center",
        WebkitMaskPosition: "center",
      }}
    />
  );
}
